<script setup lang="ts">
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import {
    destroy as destroyColumn,
    reorder as reorderColumns,
    store as storeColumn,
    update as updateColumn,
} from '@/actions/App/Http/Controllers/ExtractionColumnController';
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { useToast } from '@/composables/useToast';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Column {
    id: string;
    name: string;
    type: string;
    position: number;
    config: Record<string, unknown> | null;
}

interface Grid {
    id: string;
    columns: Column[];
}

interface Props {
    open: boolean;
    grid: Grid;
    columns: Column[];
    cellCounts?: Record<string, number>;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    (e: 'update:open', value: boolean): void;
    (e: 'columns-changed'): void;
}>();

// ─── Local state ───────────────────────────────────────────────────────────────

const toast = useToast();

function getCsrf(): string {
    return decodeURIComponent(
        document.cookie
            .split('; ')
            .find((c) => c.startsWith('XSRF-TOKEN='))
            ?.split('=')[1] ?? '',
    );
}

function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
    return fetch(url, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': getCsrf(),
            ...options.headers,
        },
    });
}
const localColumns = ref<Column[]>([...props.columns]);

const duplicateNames = computed(() => {
    const seen: Record<string, number> = {};

    for (const col of localColumns.value) {
        seen[col.name] = (seen[col.name] ?? 0) + 1;
    }

    return new Set(
        Object.entries(seen)
            .filter(([, n]) => n > 1)
            .map(([name]) => name),
    );
});

watch(
    () => props.columns,
    (cols) => {
        localColumns.value = [...cols];
    },
    { deep: true },
);

// ─── Add column form ───────────────────────────────────────────────────────────

const COLUMN_TYPES = [
    { value: 'text', label: 'Texte' },
    { value: 'number', label: 'Nombre' },
    { value: 'boolean', label: 'Booléen' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Liste' },
    { value: 'multi_select', label: 'Multi-liste' },
] as const;

const TYPE_LABELS: Record<string, string> = Object.fromEntries(
    COLUMN_TYPES.map((t) => [t.value, t.label]),
);

const newName = ref('');
const newType = ref('text');
const newOptions = ref('');
const addingColumn = ref(false);
const addError = ref<string | null>(null);
const showOptions = computed(
    () => newType.value === 'select' || newType.value === 'multi_select',
);

async function addColumn() {
    if (!newName.value.trim()) {
        addError.value = 'Le nom est requis.';

        return;
    }

    addError.value = null;
    addingColumn.value = true;

    const config = showOptions.value
        ? {
              options: newOptions.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
          }
        : {};

    try {
        const res = await apiFetch(storeColumn.url(props.grid.id), {
            method: 'POST',
            body: JSON.stringify({
                name: newName.value.trim(),
                type: newType.value,
                config,
            }),
        });

        if (!res.ok) {
            throw new Error();
        }

        newName.value = '';
        newType.value = 'text';
        newOptions.value = '';
        emit('columns-changed');
    } catch {
        addError.value = "Erreur lors de l'ajout.";
    } finally {
        addingColumn.value = false;
    }
}

// ─── Rename column ─────────────────────────────────────────────────────────────

const editingId = ref<string | null>(null);
const editingName = ref('');

function startEdit(col: Column) {
    editingId.value = col.id;
    editingName.value = col.name;
}

async function commitEdit(col: Column) {
    const trimmed = editingName.value.trim();

    if (!trimmed || trimmed === col.name) {
        editingId.value = null;

        return;
    }

    // Optimistic update
    const previous = col.name;
    col.name = trimmed;
    editingId.value = null;

    try {
        const res = await apiFetch(updateColumn.url(col.id), {
            method: 'PATCH',
            body: JSON.stringify({ name: trimmed }),
        });

        if (!res.ok) {
            throw new Error();
        }

        emit('columns-changed');
    } catch {
        col.name = previous;
        toast.error('Impossible de renommer la colonne.');
    }
}

// ─── Delete column ─────────────────────────────────────────────────────────────

const deletingColumnId = ref<string | null>(null);

async function deleteColumn(col: Column) {
    // Optimistic removal
    const snapshot = [...localColumns.value];
    localColumns.value = localColumns.value.filter((c) => c.id !== col.id);
    deletingColumnId.value = null;

    try {
        const res = await apiFetch(destroyColumn.url(col.id), {
            method: 'DELETE',
        });

        if (!res.ok) {
            throw new Error();
        }

        emit('columns-changed');
    } catch {
        localColumns.value = snapshot;
        toast.error('Impossible de supprimer la colonne.');
    }
}

// ─── Reorder (↑ / ↓) ──────────────────────────────────────────────────────────

async function moveColumn(index: number, direction: 'up' | 'down') {
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= localColumns.value.length) {
        return;
    }

    // Optimistic swap
    const cols = [...localColumns.value];
    [cols[index], cols[newIndex]] = [cols[newIndex], cols[index]];
    const snapshot = [...localColumns.value];
    localColumns.value = cols;

    try {
        const res = await apiFetch(reorderColumns.url(props.grid.id), {
            method: 'POST',
            body: JSON.stringify({ ids: cols.map((c) => c.id) }),
        });

        if (!res.ok) {
            throw new Error();
        }

        emit('columns-changed');
    } catch {
        localColumns.value = snapshot;
        toast.error('Impossible de réordonner les colonnes.');
    }
}
</script>

<template>
    <Sheet :open="open" @update:open="emit('update:open', $event)">
        <SheetContent
            class="flex w-full max-w-md flex-col gap-0 p-0 sm:max-w-md"
        >
            <SheetHeader class="border-b border-border/40 px-6 py-5">
                <SheetTitle>Gérer les colonnes</SheetTitle>
            </SheetHeader>

            <div class="flex-1 overflow-y-auto">
                <!-- Column list -->
                <div class="divide-y divide-border/40">
                    <p
                        v-if="localColumns.length === 0"
                        class="px-6 py-8 text-center text-sm text-muted-foreground"
                    >
                        Aucune colonne. Ajoutez-en une ci-dessous.
                    </p>

                    <div
                        v-for="(col, index) in localColumns"
                        :key="col.id"
                        class="flex items-center gap-2 px-4 py-2.5"
                    >
                        <!-- Reorder arrows -->
                        <div class="flex flex-col">
                            <button
                                class="rounded p-0.5 text-muted-foreground/40 transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-20"
                                :disabled="index === 0"
                                @click="moveColumn(index, 'up')"
                            >
                                <ChevronUp class="h-3.5 w-3.5" />
                            </button>
                            <button
                                class="rounded p-0.5 text-muted-foreground/40 transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-20"
                                :disabled="index === localColumns.length - 1"
                                @click="moveColumn(index, 'down')"
                            >
                                <ChevronDown class="h-3.5 w-3.5" />
                            </button>
                        </div>

                        <!-- Name (click to edit) -->
                        <div class="flex-1 overflow-hidden">
                            <Input
                                v-if="editingId === col.id"
                                v-model="editingName"
                                class="h-8 text-sm"
                                @blur="commitEdit(col)"
                                @keydown.enter="commitEdit(col)"
                                @keydown.escape="editingId = null"
                            />
                            <button
                                v-else
                                class="w-full truncate rounded px-2 py-1 text-left text-sm transition-colors hover:bg-muted"
                                @click="startEdit(col)"
                            >
                                {{ col.name }}
                            </button>
                        </div>

                        <!-- Type badge -->
                        <span
                            class="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground ring-1 ring-border/40"
                        >
                            {{ TYPE_LABELS[col.type] ?? col.type }}
                        </span>

                        <!-- Cell count badge -->
                        <span
                            class="shrink-0 rounded px-1.5 py-0.5 text-[10px] tabular-nums ring-1"
                            :class="
                                (props.cellCounts?.[col.id] ?? 0) === 0
                                    ? 'bg-muted text-muted-foreground/50 ring-border/30'
                                    : 'bg-primary/10 text-primary ring-primary/20'
                            "
                        >
                            {{ props.cellCounts?.[col.id] ?? 0 }}
                            cell{{
                                (props.cellCounts?.[col.id] ?? 0) !== 1
                                    ? 's'
                                    : ''
                            }}
                        </span>

                        <!-- Duplicate name indicator -->
                        <span
                            v-if="duplicateNames.has(col.name)"
                            class="shrink-0 rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-600 ring-1 ring-amber-500/30 dark:text-amber-400"
                        >
                            doublon
                        </span>

                        <!-- Delete button + confirm dialog -->
                        <Button
                            size="icon"
                            variant="ghost"
                            class="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                            @click="deletingColumnId = col.id"
                        >
                            <Trash2 class="h-3.5 w-3.5" />
                        </Button>

                        <ConfirmDialog
                            :open="deletingColumnId === col.id"
                            :title="`Supprimer « ${col.name} »`"
                            description="Toutes les données saisies dans cette colonne seront supprimées définitivement."
                            confirm-text="Supprimer"
                            @update:open="
                                (v) => {
                                    if (!v) deletingColumnId = null;
                                }
                            "
                            @confirm="deleteColumn(col)"
                            @cancel="deletingColumnId = null"
                        />
                    </div>
                </div>

                <Separator />

                <!-- Add column form -->
                <div class="space-y-4 px-6 py-5">
                    <p class="text-sm font-medium">Ajouter une colonne</p>

                    <div class="space-y-2">
                        <Label for="col-name">Nom</Label>
                        <Input
                            id="col-name"
                            v-model="newName"
                            placeholder="Ex. : Design de l'étude"
                            @keydown.enter="addColumn"
                        />
                    </div>

                    <div class="space-y-2">
                        <Label for="col-type">Type</Label>
                        <Select v-model="newType">
                            <SelectTrigger id="col-type">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem
                                    v-for="t in COLUMN_TYPES"
                                    :key="t.value"
                                    :value="t.value"
                                >
                                    {{ t.label }}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div v-if="showOptions" class="space-y-2">
                        <Label for="col-options">
                            Options
                            <span class="text-muted-foreground"
                                >(séparées par des virgules)</span
                            >
                        </Label>
                        <Input
                            id="col-options"
                            v-model="newOptions"
                            placeholder="Option A, Option B, Option C"
                        />
                    </div>

                    <p v-if="addError" class="text-xs text-destructive">
                        {{ addError }}
                    </p>

                    <Button
                        class="w-full"
                        :disabled="addingColumn || !newName.trim()"
                        @click="addColumn"
                    >
                        <Plus class="mr-1.5 h-4 w-4" />
                        {{
                            addingColumn
                                ? 'Ajout en cours…'
                                : 'Ajouter la colonne'
                        }}
                    </Button>
                </div>
            </div>
        </SheetContent>
    </Sheet>
</template>
